import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Clock, Inbox, ShieldCheck } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { SectionLink } from '@/components/SectionLink'
import { useApprovalInbox } from '@/features/approvals/queries'
import type { ApprovalInboxItem } from '@/features/approvals/types'
import { formatDateTime } from '@/lib/locale'

const PAGE_SIZE = 50

function formatApproverRule(rule: ApprovalInboxItem['approver_rule']) {
  if (rule?.role) return `Role: ${rule.role}`
  if (rule?.user_id) return `User #${rule.user_id}`
  return '-'
}

export function ApprovalInboxPage() {
  const [page, setPage] = useState(0)
  const { data, isLoading, isFetching } = useApprovalInbox({ limit: PAGE_SIZE, offset: page * PAGE_SIZE })
  const items = data?.items ?? []
  const total = data?.total ?? items.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const overdueOnPage = useMemo(
    () => items.filter(i => i.due_at && new Date(i.due_at) < new Date()).length,
    [items],
  )

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'Request',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => (
        <Link to={`/approvals/instances/${row.original.id}`} className="font-medium text-primary hover:underline">
          #{row.original.id}
        </Link>
      ),
    },
    {
      accessorKey: 'resource_type',
      header: 'Type',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => (
        <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{row.original.resource_type}</code>
      ),
    },
    {
      accessorKey: 'resource_summary',
      header: 'Summary',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) =>
        row.original.resource_summary || <span className="text-muted-foreground">-</span>,
    },
    {
      accessorKey: 'policy_name',
      header: 'Policy',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => row.original.policy_name || '-',
    },
    {
      accessorKey: 'requested_by_email',
      header: 'Requested by',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) =>
        row.original.requested_by_email || <span className="text-muted-foreground">-</span>,
    },
    {
      accessorKey: 'step_order',
      header: 'Step',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => (
        <span>
          #{row.original.step_order}
          <span className="ml-1 text-xs text-muted-foreground">
            ({row.original.collected_approvals}/{row.original.required_approver_count})
          </span>
        </span>
      ),
    },
    {
      accessorKey: 'approver_rule',
      header: 'Approver',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => formatApproverRule(row.original.approver_rule),
    },
    {
      accessorKey: 'due_at',
      header: 'Due',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => {
        if (!row.original.due_at) return <span className="text-muted-foreground">-</span>
        const due = new Date(row.original.due_at)
        const overdue = due < new Date()
        return (
          <span className={overdue ? 'text-destructive' : undefined}>
            {formatDateTime(row.original.due_at)}
            {overdue ? <Badge variant="danger" className="ml-2">overdue</Badge> : null}
          </span>
        )
      },
    },
    {
      accessorKey: 'requested_at',
      header: 'Requested',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => formatDateTime(row.original.requested_at),
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }: { row: { original: ApprovalInboxItem } }) => (
        <Button variant="secondary" size="sm" asChild>
          <Link to={`/approvals/instances/${row.original.id}`}>Review</Link>
        </Button>
      ),
    },
  ], [])

  return (
    <Page
      title="Approvals"
      description="Requests waiting on your action. Steps are matched to your role or directly to you; a partial quorum needs all required approvers before the ladder advances."
    >
      <Card className="mb-3">
        <div className="flex flex-wrap items-center gap-6 p-4 text-sm">
          <div className="flex items-center gap-2">
            <Inbox className="size-5 text-primary" />
            <span className="font-semibold">{total}</span>
            <span className="text-muted-foreground">awaiting your action</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="size-5 text-warning" />
            <span className="font-semibold">{overdueOnPage}</span>
            <span className="text-muted-foreground">overdue on this page</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-muted-foreground" />
            <SectionLink to="/approvals/policies">Manage policies</SectionLink>
          </div>
        </div>
      </Card>
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={CheckCircle2}
            title={page === 0 ? 'Nothing waiting on you' : 'No more requests on this page'}
            description={page === 0
              ? "When someone requests approval on a resource matched to your role, it'll show up here. Configure policies from the Policies page to start intercepting mutations."
              : 'Go back to an earlier page to see pending requests.'}
          />
        </Card>
      ) : (
        <Card>
          <div className="p-3">
            <DataTable columns={columns as never} rows={items} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3 text-sm">
            <span className="text-muted-foreground">
              Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + items.length} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page + 1 >= totalPages || isFetching}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}
    </Page>
  )
}
